import { useState } from "react";
import { toast } from "sonner";
import {
  Scissors, ShieldCheck, BadgeCheck, MapPin, Flame, Droplets, Sparkles,
  ArrowRight, CheckCircle2, Store, User,
} from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const STANDARD = [
  { icon: Flame, t: "Hot-towel prep", d: "Steam, soften, and open the follicle. The ritual that separates a shave from an experience." },
  { icon: Scissors, t: "The straight-razor pass", d: "A single, confident blade — mapped to the grain. This is the heart of RR." },
  { icon: Droplets, t: "Sanitation & safety", d: "Fresh blade every client, sealed tools, documented standard. Non-negotiable." },
  { icon: Sparkles, t: "The finish", d: "Cool towel, balm, and a mirror moment. The client leaves feeling like a gentleman." },
];

const STEPS = [
  { icon: ShieldCheck, t: "Get RR Certified", d: "You or your shop pass the RR standard — technique, sanitation, and presentation." },
  { icon: BadgeCheck, t: "Display the RR badge", d: "Run 'RR — Real Razors' as a signature service under the Barbistry name." },
  { icon: MapPin, t: "Get discovered & booked", d: "Clients find RR-certified chairs in the directory and book the premium shave." },
];

export default function RealRazors() {
  const [form, setForm] = useState({ name: "", shop: "", email: "", city: "" });
  const [who, setWho] = useState("barber");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email) { toast.error("Enter your email."); return; }
    try {
      setBusy(true);
      const nm = form.shop ? `${form.name} · ${form.shop}` : form.name;
      await api.post("/waitlist", { email: form.email, name: nm, role: who, city: form.city, source: "rr" });
      setDone(true);
    } catch {
      toast.error("Something went wrong. Try again.");
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="rr-page">
      {/* Hero */}
      <section className="relative border-b border-white/10 bg-[#0a0a0a] overflow-hidden">
        <img
          src="/brand/real-razors-hero.jpeg"
          alt="REAL RAZORS straight razor"
          className="absolute inset-y-0 right-0 h-full w-1/2 object-cover opacity-25 hidden md:block"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0a0a0a] via-[#0a0a0a]/90 to-transparent" />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 py-24">
          <div className="inline-flex items-center gap-2 border border-[#D4AF37]/40 px-3 py-1.5 mb-8">
            <Scissors size={14} className="text-[#D4AF37]" />
            <span className="text-xs font-mono uppercase tracking-widest text-[#D4AF37]">RR · Real Razors</span>
          </div>
          <h1 className="font-display text-4xl md:text-6xl font-bold max-w-3xl leading-[1.05]">
            The signature straight-razor shave — <span className="gold-text">run inside your shop.</span>
          </h1>
          <p className="mt-6 text-neutral-300 max-w-2xl leading-relaxed">
            RR isn't a product on a shelf. It's a service standard. Barbers and shops get certified to deliver
            the Real Razors experience, display the RR badge, and charge premium for the one shave every
            gentleman remembers.
          </p>
          <a href="#enroll" className="inline-block mt-8">
            <Button className="rounded-none bg-[#D4AF37] text-black hover:bg-[#F0C84B] font-semibold h-12 px-8 group" data-testid="rr-hero-cta">
              Bring RR to your chair
              <ArrowRight size={18} className="ml-2 transition-transform group-hover:translate-x-1" />
            </Button>
          </a>
        </div>
      </section>

      {/* The RR Standard */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-20">
        <p className="font-mono text-xs uppercase tracking-widest text-[#D4AF37] mb-3">The RR Standard</p>
        <h2 className="font-display text-3xl md:text-4xl font-bold max-w-2xl">Four steps. One unforgettable shave.</h2>
        <p className="mt-4 text-neutral-400 max-w-xl">Every RR-certified chair runs the same ritual — so the name means the same thing in every shop.</p>
        <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {STANDARD.map((s, i) => (
            <div key={s.t} data-testid={`rr-standard-${i}`} className="bg-[#111] border border-white/10 p-6 hover:border-[#D4AF37]/40 transition-colors">
              <div className="flex items-center justify-between">
                <s.icon className="text-[#D4AF37]" size={24} />
                <span className="font-display text-3xl font-bold text-white/10">0{i + 1}</span>
              </div>
              <h3 className="mt-4 font-display text-lg font-semibold">{s.t}</h3>
              <p className="mt-1.5 text-sm text-neutral-400">{s.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* How it works */}
      <section className="border-y border-white/10 bg-[#0a0a0a]">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-20">
          <h2 className="font-display text-3xl md:text-4xl font-bold text-center">How RR works</h2>
          <div className="mt-12 grid gap-6 md:grid-cols-3">
            {STEPS.map((s, i) => (
              <div key={s.t} data-testid={`rr-step-${i}`} className="relative text-center px-4">
                <div className="mx-auto w-14 h-14 border border-[#D4AF37]/40 flex items-center justify-center">
                  <s.icon className="text-[#D4AF37]" size={24} />
                </div>
                <h3 className="mt-5 font-display text-xl font-semibold">{s.t}</h3>
                <p className="mt-2 text-sm text-neutral-400 max-w-xs mx-auto">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Enroll */}
      <section id="enroll" className="max-w-5xl mx-auto px-5 sm:px-8 py-20">
        <div className="border border-[#D4AF37]/30 bg-gradient-to-b from-[#111] to-[#0a0a0a] p-8 md:p-12 gold-glow">
          <p className="font-mono text-xs uppercase tracking-widest text-[#D4AF37] mb-4">Founding RR Chairs</p>
          <h2 className="font-display text-3xl md:text-4xl font-bold max-w-2xl">Apply to run RR in your shop.</h2>
          <p className="mt-4 text-neutral-300 max-w-xl">
            We're onboarding the first RR-certified barbers and shops. Tell us who you are — we'll follow up
            with certification details and founding terms. No payment now.
          </p>

          {done ? (
            <div className="mt-8 flex items-center gap-3 text-[#D4AF37]" data-testid="rr-success">
              <CheckCircle2 size={26} />
              <div>
                <p className="font-display text-xl font-semibold">You're in the RR pipeline.</p>
                <p className="text-sm text-neutral-400">We'll reach out with certification steps and founding terms.</p>
              </div>
            </div>
          ) : (
            <form onSubmit={submit} className="mt-8 space-y-4" data-testid="rr-form">
              <div className="flex flex-wrap gap-2">
                {[["barber", "I'm a barber", User], ["shop", "I own a shop", Store]].map(([v, label, Icon]) => (
                  <button key={v} type="button" onClick={() => setWho(v)} data-testid={`rr-who-${v}`}
                    className={`inline-flex items-center gap-2 px-4 py-2 text-sm border transition-colors ${who === v ? "border-[#D4AF37] text-[#D4AF37] bg-[#D4AF37]/10" : "border-white/15 text-neutral-400 hover:border-white/40"}`}>
                    <Icon size={15} /> {label}
                  </button>
                ))}
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <Input value={form.name} onChange={set("name")} placeholder="Your name" data-testid="rr-name" className="bg-[#0a0a0a] border-white/15 rounded-none focus-visible:ring-[#D4AF37]" />
                <Input value={form.shop} onChange={set("shop")} placeholder="Shop name (optional)" data-testid="rr-shop" className="bg-[#0a0a0a] border-white/15 rounded-none focus-visible:ring-[#D4AF37]" />
                <Input value={form.city} onChange={set("city")} placeholder="City" data-testid="rr-city" className="bg-[#0a0a0a] border-white/15 rounded-none focus-visible:ring-[#D4AF37]" />
                <Input type="email" required value={form.email} onChange={set("email")} placeholder="Email" data-testid="rr-email" className="bg-[#0a0a0a] border-white/15 rounded-none focus-visible:ring-[#D4AF37]" />
              </div>
              <Button type="submit" disabled={busy} data-testid="rr-submit" className="rounded-none bg-[#D4AF37] text-black hover:bg-[#F0C84B] font-semibold h-12 px-8 text-base group">
                {busy ? "Submitting…" : <>Apply for RR Certification <ArrowRight size={18} className="ml-2 transition-transform group-hover:translate-x-1" /></>}
              </Button>
              <p className="text-xs text-neutral-500">We'll only email you about RR certification and launch.</p>
            </form>
          )}
        </div>
      </section>
    </div>
  );
}
